import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import {
	type AssemblyFilmKitCounts,
	countAssemblyKitParts,
} from "../../../engines/assemblyFilm/classify";
import { resolveAssemblyMeshName } from "../../../engines/assemblyFilm/meshName";
import type { AssemblyFilmStoryboard } from "../../../engines/assemblyFilm/storyboard";
import {
	applyFilmPose,
	collectFilmParts,
	type FilmPart,
	type FilmPlanRect,
	getFilmPartProgress,
	planFilmArrows,
	planFilmRects,
	restoreFilmParts,
} from "./filmPose";

export interface AssemblyFilmFrame {
	storyboard: AssemblyFilmStoryboard;
	stepIndex: number;
	/** 0..1 through the active step. */
	progress: number;
	reducedMotion: boolean;
	/**
	 * How far the active step's parts fall into place. Interior steps fall
	 * less, so a bench slat starts inside the room rather than in the roof.
	 */
	dropMeters: number;
	/** Orbit target of the active camera preset, for the scale bar. */
	cameraTarget: [number, number, number];
}

export interface AssemblyFilmSceneInfo {
	counts: AssemblyFilmKitCounts;
	rects: FilmPlanRect[];
}

const FILM_OBJECT = "assemblyFilm";
const MAX_ARROWS = 5;
const UP = new THREE.Vector3(0, 1, 0);
const ARROW_LENGTH = 0.42;

function readToken(element: Element, token: string, fallback: string): string {
	if (typeof getComputedStyle !== "function" || !(element instanceof Element)) {
		return fallback;
	}
	return getComputedStyle(element).getPropertyValue(token).trim() || fallback;
}

/**
 * What the studio hides: everything drawn that is not the sauna, plus the
 * local lights (the heater's interior glow, accent pools) that would paint
 * warm blobs on a half-built floor. The sun, fill and sky lights stay.
 */
function isSiteDrawable(object: THREE.Object3D): boolean {
	const candidate = object as THREE.Mesh & {
		isLine?: boolean;
		isPoints?: boolean;
		isSprite?: boolean;
		isPointLight?: boolean;
		isSpotLight?: boolean;
		isRectAreaLight?: boolean;
	};
	return !!(
		candidate.isMesh ||
		candidate.isLine ||
		candidate.isPoints ||
		candidate.isSprite ||
		candidate.isPointLight ||
		candidate.isSpotLight ||
		candidate.isRectAreaLight
	);
}

function isFilmOwned(object: THREE.Object3D): boolean {
	let current: THREE.Object3D | null = object;
	while (current) {
		if (current.userData[FILM_OBJECT]) return true;
		current = current.parent;
	}
	return false;
}

function createArrow(material: THREE.Material): THREE.Group {
	const arrow = new THREE.Group();
	const headLength = 0.12;
	const shaft = new THREE.Mesh(
		new THREE.CylinderGeometry(0.018, 0.018, ARROW_LENGTH - headLength, 10),
		material,
	);
	// Arrow local +Y points from tip to tail; the tip sits at the origin.
	shaft.position.y = headLength + (ARROW_LENGTH - headLength) / 2;
	const head = new THREE.Mesh(
		new THREE.ConeGeometry(0.06, headLength, 16),
		material,
	);
	head.rotation.x = Math.PI;
	head.position.y = headLength / 2;
	arrow.add(shaft, head);
	arrow.userData[FILM_OBJECT] = true;
	return arrow;
}

interface AssemblyFilmOverrideProps {
	frame: AssemblyFilmFrame | null;
	modelRevision: number;
	groundY: number | null;
	onSceneInfo?: (info: AssemblyFilmSceneInfo) => void;
	onScaleChange?: (pxPerMeter: number) => void;
}

/**
 * In-canvas half of the assembly film. With `frame` set it turns the viewer
 * into the film's studio: the site is hidden behind a plain plate with a
 * grid, earlier steps turn to ghost, the active step drops in in the accent
 * colour with arrows, later steps are hidden. With `frame` null nothing is
 * mounted, and unmounting restores every mesh, the background and the site
 * exactly — so a viewer that never plays the film pays nothing for it.
 */
export function AssemblyFilmOverride(props: AssemblyFilmOverrideProps) {
	return props.frame ? (
		<AssemblyFilmStudio {...props} frame={props.frame} />
	) : null;
}

function AssemblyFilmStudio({
	frame,
	modelRevision,
	groundY,
	onSceneInfo,
	onScaleChange,
}: AssemblyFilmOverrideProps & { frame: AssemblyFilmFrame }) {
	const scene = useThree((state) => state.scene);
	const gl = useThree((state) => state.gl);
	const invalidate = useThree((state) => state.invalidate);
	const storyboard = frame.storyboard;

	const materials = useMemo(() => {
		const element = gl.domElement;
		// Skymning in 3D: night ground, warm off-white ink for what is already
		// built, and the one amber light for what is being built now.
		const accent = new THREE.Color(readToken(element, "--led", "#f2b56b"));
		const ink = new THREE.Color(readToken(element, "--dusk-ink", "#efe9e1"));
		const ground = new THREE.Color(
			readToken(element, "--night-raised", "#1a1922"),
		);
		return {
			ghost: new THREE.MeshStandardMaterial({ color: ink, roughness: 0.92 }),
			// Not tone-mapped: AgX would wash the LED amber out to tan, and the
			// part being built has to read as the one light in the frame.
			accent: new THREE.MeshStandardMaterial({
				color: accent,
				roughness: 0.6,
				emissive: accent,
				emissiveIntensity: 0.3,
				toneMapped: false,
			}),
			arrow: new THREE.MeshBasicMaterial({ color: accent, toneMapped: false }),
			plate: new THREE.MeshStandardMaterial({ color: ground, roughness: 1 }),
			background: new THREE.Color(readToken(element, "--night", "#111018")),
		};
	}, [gl]);
	useEffect(
		() => () => {
			materials.ghost.dispose();
			materials.accent.dispose();
			materials.arrow.dispose();
			materials.plate.dispose();
		},
		[materials],
	);

	const partsRef = useRef<FilmPart[]>([]);
	const hiddenRef = useRef(new Map<THREE.Object3D, boolean>());
	const studioRef = useRef<{
		root: THREE.Group;
		plate: THREE.Mesh;
		grid: THREE.GridHelper;
		arrows: THREE.Group[];
		background: THREE.Scene["background"];
		fog: THREE.Scene["fog"];
	} | null>(null);
	// Premium modules (door leaves, glazing) load after the build; the
	// per-frame scan bumps this when it meets a sauna mesh the film has not
	// collected yet.
	const [lateRevision, setLateRevision] = useState(0);
	const collectedRef = useRef(new Set<THREE.Object3D>());
	const onSceneInfoRef = useRef(onSceneInfo);
	onSceneInfoRef.current = onSceneInfo;

	// Enter / leave the studio.
	useEffect(() => {
		const root = new THREE.Group();
		root.name = "assembly-film/studio";
		root.userData[FILM_OBJECT] = true;
		const plate = new THREE.Mesh(
			new THREE.PlaneGeometry(1, 1),
			materials.plate,
		);
		plate.rotation.x = -Math.PI / 2;
		plate.receiveShadow = true;
		plate.name = "assembly-film/plate";
		const grid = new THREE.GridHelper(1, 1);
		grid.name = "assembly-film/grid";
		root.add(plate, grid);
		const arrows = Array.from({ length: MAX_ARROWS }, () => {
			const arrow = createArrow(materials.arrow);
			arrow.visible = false;
			root.add(arrow);
			return arrow;
		});
		scene.add(root);
		studioRef.current = {
			root,
			plate,
			grid,
			arrows,
			background: scene.background,
			fog: scene.fog,
		};
		scene.background = materials.background;
		scene.fog = null;
		const hidden = hiddenRef.current;
		return () => {
			restoreFilmParts(partsRef.current);
			partsRef.current = [];
			for (const [object, visible] of hidden) object.visible = visible;
			hidden.clear();
			const studio = studioRef.current;
			if (studio) {
				scene.background = studio.background;
				scene.fog = studio.fog;
				scene.remove(studio.root);
				studio.root.traverse((object) => {
					const mesh = object as THREE.Mesh;
					if (mesh.geometry) mesh.geometry.dispose();
				});
				(studio.grid.material as THREE.Material).dispose();
			}
			studioRef.current = null;
			invalidate();
		};
	}, [scene, materials, invalidate]);

	// (Re)collect the built parts whenever the model changes underneath us.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `modelRevision` and `lateRevision` are rebuild signals, not values read in the body.
	useEffect(() => {
		const studio = studioRef.current;
		if (!studio) return;
		const parts = collectFilmParts(scene, storyboard, partsRef.current);
		partsRef.current = parts;
		collectedRef.current = new Set(parts.map((part) => part.mesh));
		if (parts.length === 0) return;

		const bounds = new THREE.Box3();
		for (const part of parts) bounds.union(part.worldBox);
		const size = bounds.getSize(new THREE.Vector3());
		const center = bounds.getCenter(new THREE.Vector3());
		const span = Math.max(size.x, size.z) + 3;
		const plateY = groundY ?? bounds.min.y;
		studio.plate.scale.set(span, span, 1);
		studio.plate.position.set(center.x, plateY - 0.002, center.z);
		// One grid line every 0.5 m, like squared drafting paper, drawn a shade
		// lighter than the plate it sits on.
		const divisions = Math.max(2, Math.round(span / 0.5));
		const plateColor = (studio.plate.material as THREE.MeshStandardMaterial)
			.color;
		const grid = new THREE.GridHelper(
			divisions * 0.5,
			divisions,
			plateColor.clone().offsetHSL(0, 0, 0.1),
			plateColor.clone().offsetHSL(0, 0, 0.05),
		);
		grid.name = "assembly-film/grid";
		grid.position.set(center.x, plateY, center.z);
		studio.root.remove(studio.grid);
		studio.grid.geometry.dispose();
		(studio.grid.material as THREE.Material).dispose();
		studio.root.add(grid);
		studio.grid = grid;

		onSceneInfoRef.current?.({
			counts: countAssemblyKitParts(
				storyboard,
				parts.map((part) => part.name),
			),
			rects: planFilmRects(parts),
		});
	}, [storyboard, modelRevision, lateRevision, groundY, scene]);

	// Pose every frame the film state changes.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `modelRevision` and `lateRevision` re-pose the freshly collected parts.
	useEffect(() => {
		const studio = studioRef.current;
		if (!studio) return;
		const parts = partsRef.current;
		applyFilmPose(parts, {
			stepIndex: frame.stepIndex,
			progress: frame.progress,
			reducedMotion: frame.reducedMotion,
			approach: UP,
			dropMeters: frame.dropMeters,
			materials,
		});
		const arrows = planFilmArrows(
			parts,
			frame.stepIndex,
			UP,
			MAX_ARROWS,
		);
		studio.arrows.forEach((arrow, index) => {
			const plan = arrows[index];
			if (!plan) {
				arrow.visible = false;
				return;
			}
			const local = frame.reducedMotion
				? 0
				: getFilmPartProgress(frame.progress, plan.order, plan.count);
			// Each arrow shows while its part is on the way, and all of them
			// in the reduced-motion still.
			arrow.visible = frame.reducedMotion || (frame.progress > 0 && local < 1);
			arrow.position.copy(plan.tip);
			arrow.quaternion.setFromUnitVectors(UP, plan.direction.clone().negate());
		});
		invalidate();
	}, [frame, modelRevision, lateRevision, materials, invalidate]);

	// Keep the site hidden (vegetation LOD and similar may re-show meshes),
	// and report the scale at the camera's focus.
	const lastScaleRef = useRef(0);
	useFrame((state) => {
		const hidden = hiddenRef.current;
		let uncollected = false;
		scene.traverse((object) => {
			if (!isSiteDrawable(object)) return;
			if (isFilmOwned(object)) return;
			if (resolveAssemblyMeshName(object)) {
				if ((object as THREE.Mesh).isMesh && !collectedRef.current.has(object)) {
					uncollected = true;
				}
				return;
			}
			if (!object.visible) return;
			if (!hidden.has(object)) hidden.set(object, object.visible);
			object.visible = false;
		});
		if (uncollected) {
			// Mark them now so one late module costs one re-collect.
			scene.traverse((object) => {
				if ((object as THREE.Mesh).isMesh && resolveAssemblyMeshName(object)) {
					collectedRef.current.add(object);
				}
			});
			setLateRevision((revision) => revision + 1);
		}
		const camera = state.camera as THREE.PerspectiveCamera;
		if (!camera.isPerspectiveCamera || !onScaleChange) return;
		const distance = camera.position.distanceTo(
			new THREE.Vector3(...frame.cameraTarget),
		);
		const visibleHeight =
			2 * distance * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
		const pxPerMeter = state.size.height / Math.max(visibleHeight, 1e-3);
		if (
			Math.abs(pxPerMeter - lastScaleRef.current) >
			lastScaleRef.current * 0.015
		) {
			lastScaleRef.current = pxPerMeter;
			onScaleChange(pxPerMeter);
		}
	});

	return null;
}
